import React from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

interface RatingDistributionChartProps {
  ratingDistribution: Record<string, number>;
  className?: string;
}

export const RatingDistributionChart: React.FC<RatingDistributionChartProps> = ({ 
  ratingDistribution, 
  className = '' 
}) => {
  // Preparar dados garantindo que todas as estrelas 1-5 existam
  const ratingsData = [];
  const colors = ['#EF4444', '#F59E0B', '#FBBF24', '#34D399', '#10B981']; // Vermelho para verde
  const labels = [];
  
  for (let i = 1; i <= 5; i++) {
    const count = ratingDistribution[i.toString()] || 0;
    ratingsData.push(count);
    labels.push(`${i} ${i === 1 ? 'Estrela' : 'Estrelas'}`);
  }

  const totalRatings = ratingsData.reduce((sum, count) => sum + count, 0);

  const data = {
    labels,
    datasets: [
      {
        data: ratingsData,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          font: {
            size: 12,
          },
          color: '#374151',
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels && data.datasets.length) {
              return data.labels.map((label, i) => {
                const count = ratingsData[i];
                const percentage = totalRatings > 0 ? ((count / totalRatings) * 100).toFixed(1) : '0.0';
                return {
                  text: `${label}: ${count} (${percentage}%)`,
                  fillStyle: colors[i],
                  strokeStyle: colors[i],
                  lineWidth: 0,
                  pointStyle: 'circle',
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      title: {
        display: true,
        text: 'Distribuição de Avaliações',
        font: {
          size: 16,
          weight: 'bold',
        },
        color: '#1F2937',
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        borderColor: '#374151',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            const count = context.parsed;
            const percentage = totalRatings > 0 ? ((count / totalRatings) * 100).toFixed(1) : '0.0';
            return `${context.label}: ${count} feedbacks (${percentage}%)`;
          },
        },
      },
    },
    cutout: '60%',
    animation: {
      animateRotate: true,
      duration: 1500,
      easing: 'easeInOutQuart',
    },
  };

  if (totalRatings === 0) {
    return (
      <div className={`flex items-center justify-center h-80 bg-gray-50 rounded-lg ${className}`}>
        <div className="text-center">
          <div className="text-gray-400 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </div>
          <p className="text-gray-500">Nenhuma avaliação disponível</p>
          <p className="text-sm text-gray-400 mt-1">Aguardando primeiros feedbacks dos usuários</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      <div className="h-80">
        <Doughnut data={data} options={options} />
      </div>
      
      {/* Estatísticas resumidas */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center border-t pt-4">
        <div>
          <div className="text-2xl font-bold text-gray-900">{totalRatings}</div>
          <div className="text-sm text-gray-500">Total de Avaliações</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-green-600">
            {totalRatings > 0 ? (((ratingsData[3] + ratingsData[4]) / totalRatings) * 100).toFixed(1) : '0.0'}%
          </div>
          <div className="text-sm text-gray-500">Positivas (4-5 ⭐)</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-blue-600">
            {totalRatings > 0 ? (ratingsData.reduce((sum, count, index) => sum + (count * (index + 1)), 0) / totalRatings).toFixed(1) : '0.0'}
          </div>
          <div className="text-sm text-gray-500">Média Geral</div>
        </div>
      </div>
    </div>
  );
};